import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Check,
  Database,
  DollarSign,
  Package,
  ReceiptText,
  Scissors,
  ShoppingCart,
  Sparkles,
  Truck,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const steps = [
  {
    path: "/dashboard",
    section: "Dashboard",
    title: "See the health of your studio",
    description: "Start here for revenue, sales, profit, and top-performing services or products for the selected period.",
    location: "Use the date range and source filter in the top bar",
    target: '[data-testid="date-range-picker"]',
    icon: BarChart3,
  },
  {
    path: "/orders",
    section: "Sales",
    title: "Add a new record sale",
    description: "Record the services and retail products a client purchased. The sale automatically feeds revenue and profit reporting.",
    location: "Click “Record sale” in the Sales page header",
    target: '[data-testid="new-invoice-btn"]',
    icon: ShoppingCart,
  },
  {
    path: "/services",
    section: "Services",
    title: "Add and manage services",
    description: "Create your menu with prices, costs, durations, categories, and the staff members who can perform each service.",
    location: "Click “Add service” in the Services page header",
    target: '[data-testid="add-service-btn"]',
    icon: Scissors,
  },
  {
    path: "/products",
    section: "Retail products",
    title: "Add retail products",
    description: "Set a product’s price, cost, stock, and reorder point. Product sales can then be included in a record sale.",
    location: "Click “Add product” in the Retail products page header",
    target: '[data-testid="add-product-btn"]',
    icon: Package,
  },
  {
    path: "/inventory",
    section: "Inventory",
    title: "Know what needs reordering",
    description: "Inventory shows current stock, stock value, and products at or below their reorder point.",
    location: "Review “Reorder needed” at the top of the page",
    target: '[data-testid="low-stock-list"]',
    icon: Boxes,
  },
  {
    path: "/vendors",
    section: "Vendors",
    title: "Keep supplier details together",
    description: "Store supplier contacts and related spend here so purchase orders and expenses stay connected.",
    location: "Click “Add vendor” in the Vendors page header",
    target: '[data-testid="add-vendor-btn"]',
    icon: Truck,
  },
  {
    path: "/purchase-orders",
    section: "Purchasing",
    title: "Create a purchase order",
    description: "Choose a vendor, add the products you need, and mark the order received when stock arrives.",
    location: "Click “New purchase order” in the page header",
    target: '[data-testid="new-po-btn"]',
    icon: Truck,
  },
  {
    path: "/agencies",
    section: "Marketing",
    title: "Track marketing partners",
    description: "Keep retainers, spend, and attributed revenue visible for agencies, creators, and other growth partners.",
    location: "Click “Add partner” in the Marketing page header",
    target: '[data-testid="add-agency-btn"]',
    icon: Sparkles,
  },
  {
    path: "/staff",
    section: "Staff",
    title: "Add your team",
    description: "Create staff profiles with roles, departments, salaries, and active status for payroll and service assignments.",
    location: "Click “Add team member” in the Staff page header",
    target: '[data-testid="add-staff-btn"]',
    icon: Users,
  },
  {
    path: "/payroll",
    section: "Payroll",
    title: "Run a payroll period",
    description: "Select a period, run payroll for active staff, then mark each entry paid after the payment is complete.",
    location: "Click “Run payroll” in the Payroll page header",
    target: '[data-testid="run-payroll-btn"]',
    icon: DollarSign,
  },
  {
    path: "/expenses",
    section: "Expenses",
    title: "Record an expense",
    description: "Capture operating costs with a date, category, and optional vendor or marketing partner assignment.",
    location: "Click “Add expense” in the Expenses page header",
    target: '[data-testid="add-expense-btn"]',
    icon: ReceiptText,
  },
  {
    path: "/insights",
    section: "AI Insights",
    title: "Review performance patterns",
    description: "Use AI Insights after choosing a reporting period to spot changes and opportunities worth investigating.",
    location: "Set the date range first, then review the insight summary",
    target: '[data-testid="refresh-insights-btn"]',
    icon: DollarSign,
  },
  {
    path: "/settings",
    section: "Settings",
    title: "Understand your workspace",
    description: "Settings explains the live-data model and keeps the planned CRM connection area ready for the future.",
    location: "Review Workspace and CRM connection cards",
    target: '[data-testid="crm-settings-card"]',
    icon: Database,
  },
];

export default function QuickStartGuide() {
  const location = useLocation();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showFinish, setShowFinish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [demoRecords, setDemoRecords] = useState(0);
  const [cardStyle, setCardStyle] = useState({});

  useEffect(() => {
    let active = true;
    api.get("/onboarding/status")
      .then(({ data }) => {
        if (!active) return;
        setDemoRecords(data.demo_records || 0);
        if (!data.completed) setOpen(true);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const stepIndex = Math.max(0, steps.findIndex((item) => item.path === location.pathname));
  const step = steps[stepIndex];
  const StepIcon = step.icon;
  const isLast = stepIndex === steps.length - 1;

  useLayoutEffect(() => {
    if (!open || showFinish) return undefined;
    let target;
    let timer;

    const positionCard = () => {
      target = document.querySelector(step.target);
      if (!target || !cardRef.current) return;

      target.classList.add("quick-start-target");
      const targetRect = target.getBoundingClientRect();
      const cardRect = cardRef.current.getBoundingClientRect();
      const margin = 12;

      if (window.innerWidth < 640) {
        setCardStyle({ left: margin, right: margin, bottom: margin, top: "auto" });
        return;
      }

      let top = targetRect.bottom + margin;
      if (top + cardRect.height > window.innerHeight - margin) {
        top = Math.max(margin, targetRect.top - cardRect.height - margin);
      }
      const left = Math.min(
        Math.max(margin, targetRect.left),
        window.innerWidth - cardRect.width - margin
      );
      setCardStyle({ top, left, right: "auto", bottom: "auto" });
    };

    timer = window.setTimeout(() => {
      target = document.querySelector(step.target);
      target?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      window.setTimeout(positionCard, 220);
    }, 80);
    window.addEventListener("resize", positionCard);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", positionCard);
      target?.classList.remove("quick-start-target");
    };
  }, [open, showFinish, step]);

  if (!open) return null;

  const navigateToStep = (index) => {
    const bounded = Math.max(0, Math.min(index, steps.length - 1));
    navigate(steps[bounded].path);
  };

  const complete = async (clearDemoData) => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/complete", { clear_demo_data: clearDemoData });
      const deleted = Object.values(data.deleted || {}).reduce((sum, count) => sum + count, 0);
      toast.success(
        clearDemoData
          ? `${deleted} demo record${deleted === 1 ? "" : "s"} cleared. Your workspace is ready.`
          : "Quick start completed. Your current records were kept."
      );
      setOpen(false);
      setTimeout(() => window.location.reload(), 450);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not complete quick start. Please try again.");
      setSaving(false);
    }
  };

  return createPortal((
    <aside
      key={step.path}
      ref={cardRef}
      className="quick-start-card"
      style={showFinish ? {} : cardStyle}
      role="dialog"
      aria-modal="false"
      aria-labelledby="quick-start-title"
      data-testid="quick-start-guide"
    >
      <div className="quick-start-pointer" aria-hidden="true" />
      {!showFinish ? (
        <>
          <header className="quick-start-header">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="quick-start-mini-icon"><StepIcon /></div>
              <div className="min-w-0">
                <div className="quick-start-kicker">Quick start</div>
                <div className="quick-start-count">{stepIndex + 1} of {steps.length} · {step.section}</div>
              </div>
            </div>
            <button type="button" className="quick-start-close" onClick={() => setShowFinish(true)} aria-label="Exit quick start">
              <X />
            </button>
          </header>
          <div className="quick-start-progress" aria-hidden="true">
            {steps.map((item, index) => (
              <button
                type="button"
                key={item.title}
                className={index <= stepIndex ? "is-active" : ""}
                onClick={() => navigateToStep(index)}
                aria-label={`Go to ${item.section}`}
              />
            ))}
          </div>
          <div className="quick-start-body">
            <div className="quick-start-location">{step.location}</div>
            <h2 id="quick-start-title">{step.title}</h2>
            <p>{step.description}</p>
          </div>
          <footer className="quick-start-footer">
            <button type="button" className="quick-start-skip" onClick={() => setShowFinish(true)}>
              Exit
            </button>
            <div className="quick-start-actions">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigateToStep(stepIndex - 1)}
                disabled={stepIndex === 0}
                className="rounded-xl"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => isLast ? setShowFinish(true) : navigateToStep(stepIndex + 1)}
                className="rounded-xl bg-[#5C4035] hover:bg-[#442E26]"
              >
                {isLast ? "Finish" : `Next: ${steps[stepIndex + 1].section}`} <ArrowRight />
              </Button>
            </div>
          </footer>
        </>
      ) : (
        <div className="quick-start-finish">
          <button type="button" className="quick-start-close quick-start-finish-close" onClick={() => setShowFinish(false)} aria-label="Return to quick start">
            <X />
          </button>
          <div className="quick-start-mini-icon quick-start-finish-icon"><Database /></div>
          <div className="quick-start-eyebrow">One-time setup</div>
          <h2 id="quick-start-title">Clear demo data?</h2>
          <p>
            Only records explicitly marked as demo data can be removed. Anything entered or edited in the platform stays safe and persistent.
          </p>
          <div className="quick-start-safety">
            <Boxes />
            <span><strong>{demoRecords} marked demo records</strong> found in this workspace</span>
          </div>
          <div className="quick-start-finish-actions">
            <Button type="button" variant="outline" onClick={() => complete(false)} disabled={saving} className="rounded-xl">
              Keep data
            </Button>
            <Button
              type="button"
              onClick={() => complete(true)}
              disabled={saving || demoRecords === 0}
              className="rounded-xl bg-[#9C4F45] hover:bg-[#824239]"
            >
              {saving ? "Finishing…" : "Clear & finish"}
            </Button>
          </div>
          <button type="button" className="quick-start-return" onClick={() => setShowFinish(false)} disabled={saving}>
            Return to guide
          </button>
        </div>
      )}
    </aside>
  ), document.body);
}